import type { TBenchmarkingResponse } from '@/react-query/insights/types';

import { useGetFilteredClinicalData } from '../data/getClinicalData';

import { calculateRates } from './calculateRates';
import { useProviderCounts } from './providerRates';
import type { TBenchmarkingCounts } from './types';

/*
Get counts per provider and then aggregate to the practice level.
 */
export const usePracticeCounts = (data?: TBenchmarkingResponse) => {
  const providerCounts = useProviderCounts(data);

  const initPracticeCounts: (TBenchmarkingCounts & {
    practiceId: string;
    practiceName?: string;
  })[] = [];

  const practiceCounts = providerCounts?.reduce((acc, provider) => {
    if (!provider?.practiceId) {
      return acc;
    }
    const practice = acc.find((p) => p.practiceId === provider?.practiceId);
    const recommendations = provider?.recommendations;
    const previousRecommendations = provider?.previousRecommendations;

    if (practice) {
      practice.recommendations.planned += recommendations?.planned ?? 0;
      practice.recommendations.scheduled += recommendations?.scheduled ?? 0;
      practice.recommendations.completed += recommendations?.completed ?? 0;
      practice.recommendations.total += recommendations?.total ?? 0;
      practice.recommendations.unplanned += recommendations?.unplanned ?? 0;
      practice.previousRecommendations.planned +=
        previousRecommendations?.planned ?? 0;
      practice.previousRecommendations.scheduled +=
        previousRecommendations?.scheduled ?? 0;
      practice.previousRecommendations.completed +=
        previousRecommendations?.completed ?? 0;
      practice.previousRecommendations.total +=
        previousRecommendations?.total ?? 0;
      practice.previousRecommendations.unplanned +=
        previousRecommendations?.unplanned ?? 0;
      practice.completedProduction += provider?.completedProduction ?? 0;
      practice.previousCompletedProduction +=
        provider?.previousCompletedProduction ?? 0;
      practice.recommendations.viewedInVidea +=
        recommendations?.viewedInVidea ?? 0;
      practice.previousRecommendations.viewedInVidea +=
        previousRecommendations?.viewedInVidea ?? 0;
    } else {
      acc.push({
        practiceId: provider?.practiceId,
        practiceName: provider?.practiceName,
        completedProduction: provider?.completedProduction ?? 0,
        previousCompletedProduction: provider?.previousCompletedProduction ?? 0,
        recommendations: {
          planned: recommendations?.planned ?? 0,
          scheduled: recommendations?.scheduled ?? 0,
          completed: recommendations?.completed ?? 0,
          total: recommendations?.total ?? 0,
          unplanned: recommendations?.unplanned ?? 0,
          viewedInVidea: recommendations?.viewedInVidea ?? 0,
        },
        previousRecommendations: {
          planned: previousRecommendations?.planned ?? 0,
          scheduled: previousRecommendations?.scheduled ?? 0,
          completed: previousRecommendations?.completed ?? 0,
          total: previousRecommendations?.total ?? 0,
          unplanned: previousRecommendations?.unplanned ?? 0,
          viewedInVidea: previousRecommendations?.viewedInVidea ?? 0,
        },
      });
    }
    return acc;
  }, initPracticeCounts);

  return practiceCounts;
};

/*
Get counts per practice and then calculate the rates.
 */
export const usePracticeRates = ({ practices }: { practices?: string[] }) => {
  const { data, isLoading } = useGetFilteredClinicalData({});
  const practiceCounts = usePracticeCounts(data)?.filter((practice) =>
    practices
      ? practice?.practiceId && practices.includes(practice?.practiceId)
      : true,
  );

  const practiceRates = practiceCounts?.map((practice) => {
    return {
      practiceName: practice.practiceName,
      practiceId: practice.practiceId,
      ...calculateRates(practice),
    };
  });

  return { practiceRates, isLoading };
};
